import { HTMLAttributes } from 'react';
import React from 'react';

import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import {
  BLOCKS, Document, INLINES
} from '@contentful/rich-text-types';
import clsx from 'clsx';

import Link from './atoms/Link';

interface Props extends HTMLAttributes<HTMLDivElement> {
  document: Document;
  options?: Options
}

const renderListItem = (_node: unknown, children: React.ReactNode) => {
  const childArray = React.Children.toArray(children);

  // If there's a single child and it's a paragraph, render its contents directly.
  if (
    childArray.length === 1 &&
    React.isValidElement(childArray[0]) &&
    childArray[0].type === 'p'
  ) {
    const paragraphContent = React.Children.toArray(childArray[0].props.children);

    return <li className="mb-xs">{paragraphContent}</li>;
  }

  // Otherwise, render the children normally inside the list item.
  return <li className="mb-xs">{children}</li>;
};

export const defaultRenderOptions: Options = {
  renderNode: {
    [BLOCKS.LIST_ITEM]: renderListItem,
    [BLOCKS.OL_LIST]: (node, children) => <ol className="list-decimal pl-md">{children}</ol>,
    [BLOCKS.UL_LIST]: (node, children) => <ul className="list-disc pl-md">{children}</ul>,

    // White underline text
    [INLINES.HYPERLINK]: (node, children) => {
      return (
        <Link
          className="text-text-primary underline"
          disableDefaultStyle
          href={node.data.uri}
        >
          {children}
        </Link>
      );
    },
  },
};

const CfRichText = ({
  className,
  document,
  options,
  ...restProps
}: Props) => {
  return (
    <div className={clsx('flex flex-col gap-sm', className)} {...restProps}>
      {documentToReactComponents(document, {
        ...defaultRenderOptions,
        ...options,
      })}
    </div>
  );
};

export default CfRichText;
